const PAGE_SIZE = 16;
const SEARCH_DEBOUNCE_MS = 300;

function debounce(fn, delay) {
	let timeoutId;
	return (...args) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	};
}

async function fetchCakes(block, { tagId, search, offset, append }) {
	const url = new URL(block.dataset.loadUrl, window.location.origin);
	if (tagId) {
		url.searchParams.set('tag', tagId);
	}
	if (search) {
		url.searchParams.set('search', search);
	}
	url.searchParams.set('offset', String(offset));

	const response = await fetch(url.toString(), {
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
	});

	if (!response.ok) {
		throw new Error('Failed to load cakes');
	}

	return response.json();
}

function setLoading(block, isLoading) {
	block.dataset.loading = isLoading ? 'true' : 'false';
	block.querySelector('[data-all-cakes-loading]')?.classList.toggle('hidden', !isLoading);
}

function setEmpty(block, isEmpty) {
	block.querySelector('[data-all-cakes-empty]')?.classList.toggle('hidden', !isEmpty);
}

function initAllCakesBlock(block) {
	const grid = block.querySelector('[data-all-cakes-grid]');
	const sentinel = block.querySelector('[data-all-cakes-sentinel]');
	const searchInput = block.querySelector('[data-all-cakes-search]');
	const tagButtons = block.querySelectorAll('[data-cake-tag-filter]');

	if (!grid || !block.dataset.loadUrl) {
		return;
	}

	const state = {
		tagId: '',
		search: '',
		offset: grid.children.length,
		hasMore: block.dataset.initialHasMore === 'true',
		loading: false,
	};

	const load = async ({ append = false } = {}) => {
		if (state.loading) {
			return;
		}

		if (!append) {
			state.offset = 0;
			state.hasMore = true;
		}

		if (append && !state.hasMore) {
			return;
		}

		state.loading = true;
		setLoading(block, true);

		try {
			const data = await fetchCakes(block, {
				tagId: state.tagId,
				search: state.search,
				offset: state.offset,
				append,
			});

			const html = typeof data.html === 'string' ? data.html : '';

			if (append) {
				grid.insertAdjacentHTML('beforeend', html);
			} else {
				grid.innerHTML = html;
			}

			state.offset += PAGE_SIZE;
			state.hasMore = data.hasMore;
			setEmpty(block, data.total === 0);
		} catch (error) {
			console.error(error);
		} finally {
			state.loading = false;
			setLoading(block, false);
		}
	};

	tagButtons.forEach(button => {
		button.addEventListener('click', () => {
			const tagId = button.dataset.tagId || '';

			if (state.tagId === tagId) {
				return;
			}

			state.tagId = tagId;
			tagButtons.forEach(btn => btn.classList.toggle('active', btn === button));
			load({ append: false });
		});
	});

	if (searchInput) {
		const handleSearch = debounce(() => {
			const search = searchInput.value.trim();
			if (search === state.search) {
				return;
			}
			state.search = search;
			load({ append: false });
		}, SEARCH_DEBOUNCE_MS);

		searchInput.addEventListener('input', handleSearch);
	}

	if (sentinel && 'IntersectionObserver' in window) {
		const observer = new IntersectionObserver(entries => {
			if (entries.some(entry => entry.isIntersecting)) {
				load({ append: true });
			}
		}, {
			rootMargin: '200px 0px',
		});

		observer.observe(sentinel);
	}
}

export function initAllCakesBlocks() {
	document.querySelectorAll('[data-all-cakes-block]').forEach(initAllCakesBlock);
}
